import { ZodIssue, ZodIssueCode, ZodParsedType } from 'zod';
import { TextKey } from '@mews-framework/localization';
import { CustomErrorParams, CustomErrorType } from './CustomErrorType';

export interface IssueMapperResult {
    textKey: Mews.TextKey;
    substitutes?: Record<string, string | number>;
    message: string;
}

export const issueMapper = (issue: ZodIssue): IssueMapperResult => {
    const { message } = issue;
    if (message === TextKey.Required) {
        return { textKey: TextKey.ValidateRequired, message: TextKey.ValidateRequired };
    }

    switch (issue.code) {
        case ZodIssueCode.invalid_type: {
            if (issue.received === ZodParsedType.undefined || issue.received === ZodParsedType.null) {
                return { textKey: TextKey.ValidateRequired, message };
            }
            if (issue.expected === ZodParsedType.number || issue.expected === ZodParsedType.integer) {
                return { textKey: TextKey.ValidateNumber, message };
            }
            break;
        }
        case ZodIssueCode.invalid_union: {
            if (issue.unionErrors[0]?.issues?.[0]) {
                return issueMapper(issue.unionErrors[0].issues[0]);
            }
            break;
        }
        case ZodIssueCode.invalid_date:
            return { textKey: TextKey.ValidateDate, message };
        case ZodIssueCode.too_small: {
            switch (issue.type) {
                case ZodParsedType.number: {
                    if (issue.inclusive) {
                        return {
                            textKey: TextKey.NumberNotGreaterThanOrEqualToErrorMessage,
                            substitutes: { number: Number(issue.minimum) },
                            message,
                        };
                    } else if (issue.exact) {
                        return { textKey: TextKey.ValidateNumber, message };
                    }
                    return { textKey: TextKey.ValidateNumber, message };
                }
                case ZodParsedType.string: {
                    return { textKey: issue.minimum === 1 ? TextKey.ValidateRequired : TextKey.InputError, message };
                }
                case ZodParsedType.array: {
                    /**
                     * "too_small" error for arrays will refer to length of the array,
                     * this will typically mean that no value is selected in a multiselect.
                     */
                    return { textKey: TextKey.ValidateRequired, message };
                }
                case ZodParsedType.date:
                    return { textKey: TextKey.ValidateDate, message };
                default:
            }
            break;
        }
        case ZodIssueCode.too_big: {
            switch (issue.type) {
                case ZodParsedType.number:
                    return {
                        textKey: TextKey.ValidateNumberUpperBound,
                        substitutes: { upperBound: Number(issue.maximum) },
                        message,
                    };
                case ZodParsedType.string:
                    return {
                        textKey: TextKey.MaximumTextLengthErrorMessage,
                        substitutes: { maximum: Number(issue.maximum) },
                        message,
                    };
                case ZodParsedType.date:
                    return { textKey: TextKey.ValidateDate, message };
                default:
            }
            break;
        }
        case ZodIssueCode.invalid_string: {
            switch (issue.validation) {
                case 'datetime':
                    return { textKey: TextKey.ValidateDate, message };
                case 'email':
                    return { textKey: TextKey.ValidateEmail, message };
                case 'url':
                    return { textKey: TextKey.InvalidUrlErrorMessage, message };
                case 'uuid':
                    return { textKey: TextKey.ValidateGUID, message };
                default:
            }
            break;
        }
        case ZodIssueCode.custom: {
            const errorParams = issue.params as CustomErrorParams | undefined;
            switch (errorParams?.errorType) {
                case CustomErrorType.TimeString:
                    return { textKey: TextKey.ValidateTime, message };
                case CustomErrorType.DateString:
                    return { textKey: TextKey.ValidateDate, message };
                case CustomErrorType.DateRange:
                    return { textKey: TextKey.InvalidServiceOrderEndErrorMessage, message };
                case CustomErrorType.BirthDateString:
                    return {
                        textKey: TextKey.BirthDateInvalidErrorMessage,
                        substitutes: { minBirthDate: errorParams.minBirthDate },
                        message,
                    };
                case CustomErrorType.FormattedNumber:
                    return { textKey: TextKey.ValidateFormattedNumber, message };
                case CustomErrorType.FileSize:
                    return {
                        textKey: TextKey.FileSizeLargerThanAllowed,
                        substitutes: { size: errorParams.maxSize },
                        message,
                    };
                case CustomErrorType.Other:
                    return {
                        textKey: errorParams.textKey || TextKey.InputError,
                        substitutes: errorParams.substitutes,
                        message,
                    };
                default:
            }
            break;
        }
        case ZodIssueCode.invalid_literal: {
            if (issue.expected === true) {
                return {
                    textKey: TextKey.ValidateRequired,
                    message,
                };
            }
            break;
        }
        default:
    }

    return { textKey: TextKey.InputError, message };
};
